/** biome-ignore-all lint/style/useNamingConvention: allow snake case */
import {ReactNode, useMemo} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {selectEventLinks} from '~/common/event/eventAttributes/reducer';
import {EventLink} from '~/common/event/types';
import {selectPendingHostIds} from '~/common/fabric/reducers/eviteEvent/selectors';
import {pluralizeWord, prettyFormatPhoneNumber} from '~/common/utils';
import {isDraft} from '~/common/utils/event_utils';
import {
  selectCohost,
  selectGuestsOfHonor,
  selectHostIds,
  selectHostName,
  selectPhoneNumber,
} from '~/ViewInvitation/selectors/eventDetails';
import {selectStatus} from '~/ViewInvitation/selectors/eviteEvent';
import {isHost} from '~/ViewInvitation/utils';
import {normalizeGuestsOfHonor} from '~/ViewInvitation/utils/guests';
import {Typography} from '~library/atoms/Typography';
import {CrownIcon} from '~library/svg/v2/CrownIcon';
import {LinkIcon} from '~library/svg/v2/LinkIcon';
import {PhoneIcon} from '~library/svg/v2/PhoneIcon';
import {UserIcon} from '~library/svg/v2/UserIcon';
import {HostDetailsSection} from './HostDetailsSection';

import './HostDetails.sass';

interface HostDetailsProps {
  readonly?: boolean;
  disableContent?: boolean;
}

export const HostDetails = ({readonly = true, disableContent = false}: HostDetailsProps) => {
  const hostName = useSelector(selectHostName);
  const hostIds = useSelector(selectHostIds, shallowEqual);
  const cohost = useSelector(selectCohost, shallowEqual);
  const phoneNumber = useSelector(selectPhoneNumber);
  const status = useSelector(selectStatus);
  const pendingHostIds = useSelector(selectPendingHostIds);
  const guestsOfHonor = useSelector(selectGuestsOfHonor, shallowEqual);
  const gohs = normalizeGuestsOfHonor(guestsOfHonor);

  const eventLinks: EventLink[] = useSelector(selectEventLinks, shallowEqual);

  const hostNames: ReactNode = useMemo(() => {
    if (hostIds.length > 1 && pendingHostIds.length === 0 && cohost.email) {
      return `${hostName} & ${cohost.name}`;
    }
    const hasUnsavedCohost = !pendingHostIds.length && cohost?.email && cohost?.name;
    if ((isDraft(status) || isHost()) && (pendingHostIds.length > 0 || hasUnsavedCohost)) {
      return (
        <>
          {hostName}{' '}
          <Typography as="span" variant="paragraph3" color="coin">
            +{hasUnsavedCohost ? 1 : pendingHostIds.length} (Pending)
          </Typography>
        </>
      );
    }
    return hostName;
  }, [hostIds, pendingHostIds, cohost, status, hostName]);

  return (
    <div className="view-invitation__details-module__host-details">
      {/* HOST DETAILS */}
      <HostDetailsSection
        title="Host Details"
        titleQaId="hosted-by"
        lines={[
          {
            icon: UserIcon,
            value: hostNames,
            type: 'text',
            key: 'hostname',
            qaId: 'hostname',
          },
          {
            icon: PhoneIcon,
            display_text: prettyFormatPhoneNumber(phoneNumber),
            value: `tel:1${phoneNumber}`,
            type: 'link',
            key: 'phone-number',
            qaId: 'phone-number',
            hide: !phoneNumber,
          },
        ]}
        readonly={readonly}
        disableContent={disableContent}
      />

      {/* GUEST(S) OF HONOR */}
      <HostDetailsSection
        title={`${pluralizeWord('Guest', gohs.length)} of Honor`}
        titleQaId="guests-of-honor"
        lines={gohs.map((guest: {email: string; name: string; userId: string}) => ({
          icon: CrownIcon,
          key: guest.email || guest.userId || guest.name,
          value: guest.email || guest.userId || guest.name,
          display_text: guest.name,
          type: 'text',
          qaId: 'guest-of-honor',
        }))}
        readonly={readonly}
        disableContent={disableContent}
      />

      {/* LINKS */}
      <HostDetailsSection
        title="Links"
        titleQaId="additional-links"
        lines={eventLinks.map((link, index) => ({
          icon: LinkIcon,
          key: link.url,
          value: link.url,
          display_text: link.display_name || link.url,
          type: 'link',
          qaId: `event-link-${index}`,
        }))}
        readonly={readonly}
        disableContent={disableContent}
      />
    </div>
  );
};
